/**
 * OneDriveCard Component
 * Displays OneDrive authentication status and configuration
 */

import React, { useState, useEffect } from 'react';
import { Cloud, CheckCircle, XCircle, LogOut, Loader } from 'lucide-react';
import * as oneDriveService from '../../services/oneDriveAppService';
import './OneDriveCard.css';

const OneDriveCard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userInfo, setUserInfo] = useState(null);
  const [authenticating, setAuthenticating] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const authenticated = await oneDriveService.isAuthenticated();
      setIsAuthenticated(authenticated);
      
      if (authenticated) {
        const info = await oneDriveService.getUserInfo();
        setUserInfo(info);
      }
    } catch (err) {
      console.error('Error checking auth status:', err);
      setError('Error al verificar estado de autenticación');
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    setAuthenticating(true);
    setError(null);
    
    try {
      const authUrl = await oneDriveService.getAuthUrl();
      
      // Open auth URL in popup
      const width = 600;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;
      
      const popup = window.open(
        authUrl,
        'OneDrive Authentication',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      // Listen for auth callback
      const handleMessage = async (event) => {
        if (event.data.type === 'onedrive-auth-success' && event.data.code) {
          try {
            await oneDriveService.completeAuthentication(event.data.code);
            await checkAuthStatus();
            popup.close();
          } catch (err) {
            setError('Error al completar autenticación: ' + err.message);
          } finally {
            setAuthenticating(false);
            window.removeEventListener('message', handleMessage);
          }
        }
      };

      window.addEventListener('message', handleMessage);
      
      // Check if popup was closed
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          setAuthenticating(false);
          window.removeEventListener('message', handleMessage);
        }
      }, 500);
      
    } catch (err) {
      setError('Error al iniciar autenticación: ' + err.message);
      setAuthenticating(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('¿Está seguro de desconectar su cuenta de OneDrive?')) {
      return;
    }

    setDisconnecting(true);
    setError(null);
    
    try {
      await oneDriveService.logout();
      setIsAuthenticated(false);
      setUserInfo(null);
    } catch (err) {
      setError('Error al desconectar: ' + err.message);
    } finally {
      setDisconnecting(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('es-CL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="onedrive-card">
        <div className="onedrive-card-loading">
          <Loader size={24} className="spinning" />
          <span>Verificando conexión...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="onedrive-card">
      <div className="onedrive-card-header">
        <div className="onedrive-card-title">
          <Cloud size={24} />
          <h3>OneDrive</h3>
        </div>
        <div className={`onedrive-status ${isAuthenticated ? 'connected' : 'disconnected'}`}>
          {isAuthenticated ? (
            <>
              <CheckCircle size={18} />
              <span>Conectado</span>
            </>
          ) : (
            <>
              <XCircle size={18} />
              <span>Desconectado</span>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="onedrive-error">
          <XCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      <div className="onedrive-card-content">
        {isAuthenticated && userInfo ? (
          <>
            <div className="onedrive-info-grid">
              <div className="onedrive-info-item">
                <label>Usuario conectado:</label>
                <span>{userInfo.email}</span>
              </div>
              
              <div className="onedrive-info-item">
                <label>Fecha de conexión:</label>
                <span>{formatDate(userInfo.tokenCreatedAt)}</span>
              </div>
            </div>

            <button
              className="onedrive-disconnect-button"
              onClick={handleDisconnect}
              disabled={disconnecting}
            >
              {disconnecting ? (
                <>
                  <Loader size={18} className="spinning" />
                  <span>Desconectando...</span>
                </>
              ) : (
                <>
                  <LogOut size={18} />
                  <span>Desconectar usuario</span>
                </>
              )}
            </button>
          </>
        ) : (
          <div className="onedrive-not-connected">
            <p>No hay ninguna cuenta de OneDrive conectada.</p>
            <p className="onedrive-description">
              Conecte su cuenta de Microsoft para gestionar archivos de presupuesto en OneDrive.
            </p>
            <button
              className="onedrive-connect-button"
              onClick={handleConnect}
              disabled={authenticating}
            >
              {authenticating ? (
                <>
                  <Loader size={18} className="spinning" />
                  <span>Conectando...</span>
                </>
              ) : (
                <>
                  <Cloud size={18} />
                  <span>Conectar con OneDrive</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OneDriveCard;
